<template>
    <div class="page">
        <div class="search-info">
            <div class="search-results">
                <p class="primary">Search results for <strong>&ldquo;{{ searchQuery }}&rdquo;</strong></p>
                <p v-if="numEvents === 1" class="secondary">{{ numEvents }} event matched your search</p>
                <p v-if="numEvents > 1" class="secondary">{{ numEvents }} events match your search</p>
                <div class="suggestions-header" v-if="suggestions.length">
                    <p>
                        Did you mean:
                        <span
                            v-for="(suggestion, index) in suggestions"
                            :key="suggestion.id">
                            <router-link :to="makeEntityRoute(suggestion.type, suggestion)">{{ suggestion.name }}</router-link><template v-if="index !== suggestions.length - 1">, </template>
                        </span>
                    </p>
                </div>
            </div>
            <div class="date-filter">
                <span class="icon-calendar-blank"></span>
                <fx-date-range
                    align="right"
                    data-e2e="search.date-filter"
                    min-date="today"
                    v-model="dateRange"
                    :custom-option-copy="'Choose Date'"
                    :default-option-copy="'Any Date'"
                    :presets="dateRangeFilterPresets"
                    @input="onDateRangeSelected">
                    <span class="icon-chevron-lg-down" slot="caret"></span>
                </fx-date-range>
            </div>
        </div>
        <EventList
            :events="events"
            :format-points="formatPoints"
            :is-loading-events="isLoadingEvents"
            :is-loading-more-events="isLoadingMoreEvents"
            :is-loyalty-pricing="$store.state.isRedeemingLoyaltyPoints"
            :load-more-copy="'Show More'"
            :loading-more-copy="'Loading More Events...'"
            :make-event-route="makeEventRoute"
            :pagination="pagination"
            @load-more-events="loadMoreEvents"
        />
    </div>
</template>

<script>
import { makeEntityRoute } from '../services/EntityService'
import DateUtil from 'core/utilities/DateUtil'
import errorHandleable from '../mixins/ErrorHandleable'
import eventFilterable from '../mixins/EventFilterable'
import eventListable from '../mixins/EventListable'
import loyaltyFormattable from '../mixins/LoyaltyFormattable'
import quebecUserModal from '../mixins/QuebecUserModal'
import trackable from '../mixins/Trackable'

export default {
    beforeCreate () {
        this.htmlPageTitle = `Search at ${this.$config.partnerName}`
    },
    created () {
        this.trackPageView(this.$route.fullPath, this.htmlPageTitle)
    },
    data () {
        return {
            numEvents: 0,
            suggestions: []
        }
    },
    head: {
        title () {
            return {
                inner: this.htmlPageTitle
            }
        }
    },
    methods: {
        fetchEvents () {
            return this.$store.state.userLocationPromise
                .then(userLocation => {
                    return this.$marketplace.search(this.searchQuery, {
                        all_in_price: this.$config.isAllInPricing ? 1 : 0,
                        date_end: this.serializedEndDate,
                        date_start: this.serializedStartDate,
                        per_page: this.$config.searchPerPage,
                        page: this.page,
                        utc_offset: DateUtil.format(new Date(), 'Z')
                    })
                })
                .then(response => {
                    this.numEvents = response.chronological.meta.total
                    this.suggestions = response.did_you_mean.results.slice(0, 3)

                    return {
                        events: response.chronological.results,
                        pagination: response.chronological.meta
                    }
                })
        },
        makeEntityRoute
    },
    mixins: [
        errorHandleable,
        eventFilterable,
        eventListable,
        loyaltyFormattable,
        quebecUserModal,
        trackable
    ]
}
</script>

<style lang="postcss" scoped>
@import 'theme';

.page {
    padding: 8px var(--site-padding);
    width: 100%;

    @media (--mq-full-min) {
        margin: 0 auto;
        width: var(--site-container-width);
    }
}

.search-info {
    display: flex;
    flex-direction: column;
    @media (--mq-large-min) {
        flex-direction: row;
    }
}

.search-results {
    -webkit-font-smoothing: antialiased;
    font-weight: var(--fw-light);
    width: 90%;

    & .primary {
        font-size: var(--fs-largest);
    }

    & .secondary {
        font-weight: var(--fw-light);
        margin-top: 0;
        margin-bottom: 12px;
    }
}

.suggestions-header {
    display: flex;
    justify-content: space-between;
    margin-top: -12px;
}

.date-filter {
    padding-left: 24px;
    padding-top: 8px;
    position: relative;
    align-self: flex-end;

    & .fx-date-range {
        display: inline-block;
        padding-bottom: 10px;
    }

    & .icon-calendar-blank {
        color: #1c74a5;
        font-size: 18px;
        left: 0;
        position: absolute;
        top: 12px;
    }

    & >>> .fx-selection {
        color: #1c74a5;
        font-size: 16px;
        text-decoration: underline;
        margin-right: 2px !important;
    }

    & >>> .icon-cancel {
        color: #1c74a5;
        font-size: 14px;
        position: relative;
        right: 0;
        top: 2px;
    }

    & >>> .fx-select-input {
        border-bottom: 0;
    }

    & >>> .icon-chevron-lg-down {
        color: #1c74a5;
        font-size: 14px;
        position: relative;
        top: 2px;
    }

    & >>> .fx-select-dropdown {
        & li {
            padding: 10px 16px;

            &.selected,
            &:active,
            &:focus,
            &:hover {
                background: linear-gradient(-90deg, rgba(60, 172, 209, 0.00) 0%, rgba(28, 116, 165, 0.13) 100%);
                border-left: 4px solid #1c74a5;
                color: #1c74a5;
                padding-left: 12px;
            }
        }
    }
}
</style>
